import { faBuilding, faPen, faPerson } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GridColDef } from '@mui/x-data-grid-pro'
import { IconButton, Link, Stack, Typography } from '@vapor/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HicDataGridPro } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { keysOf } from 'src/utils/utils'
import { ReservationBillDocumentF } from '../../../../types/reservation-detail.types'
import { useHicCommonDataGridProps } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { HiCIcon } from 'src/app/components/icons/icon'
import { BillingRow } from './billing-grid.types'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import getFiscalTypeDescription from 'src/app/pages/accounting/documents/documents-utils'
import { Box } from '@mui/material'

export type BillingGridProps = {
  documents: ReservationBillDocumentF[]
  onEditClick: (documentId: string) => void
  readOnly: boolean
}

export default function BillingGrid({ readOnly, documents, onEditClick }: BillingGridProps) {
  const columns = useColumns(onEditClick, readOnly)
  const { md } = useBreakpoints()
  const rows = useRows(documents)
  const { hicCommonDataGridProProps } = useHicCommonDataGridProps()
  return (
    <HicDataGridPro //
      {...hicCommonDataGridProProps}
      columns={md ? columns : columns.filter((col) => !col.hideable)}
      rows={rows}
      autoHeight
      hideFooter
    />
  )
}

function useColumns(onEditClick: (documentId: string) => void, readOnly: boolean): GridColDef[] {
  const { t } = useTranslation()
  const f = keysOf<BillingRow>()
  const formatDate = useFormatDate()
  const { formatCurrency } = useFormatCurrency()
  const { md } = useBreakpoints()

  return [
    {
      field: f('numberExtended'),
      headerName: t('pms.booking.reservation.deposits.document'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as BillingRow
        return (
          <Stack direction="column">
            <Typography variant="caption">{getFiscalTypeDescription(row.fiscalType, t).toUpperCase()}</Typography>
            <Link style={{ pointerEvents: readOnly ? 'none' : undefined, color: readOnly ? 'var(--Cadet)' : undefined }} onClick={() => onEditClick(params.row.id)}>
              {params.value}
            </Link>
          </Stack>
        )
      },
    },
    {
      field: f('date'),
      headerName: t('pms.booking.reservation.billing.date'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      hideable: true,
      renderCell: (params) => {
        const date = (params.row as BillingRow).date
        return formatDate(date)
      },
    },
    {
      field: f('customer'),
      headerName: t('pms.booking.reservation.deposits.type.owner'),
      flex: md ? 3 : 2,
      minWidth: 150,
      disableColumnMenu: true,
      renderCell: (params) => {
        const customer = (params.row as BillingRow).customer
        return (
          <Stack direction="column">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <HiCIcon>
                <FontAwesomeIcon icon={customer.type === 'PERSON' ? faPerson : faBuilding} />
              </HiCIcon>
              <Typography variant="body">{customer.name}</Typography>
            </Stack>
            <Typography variant="caption">{customer.fiscalCode}</Typography>
          </Stack>
        )
      },
    },
    {
      field: f('amount'),
      headerName: t('pms.booking.reservation.billing.total_amount'),
      flex: 1,
      minWidth: 100,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as BillingRow
        return <Box sx={{ color: row.amount < 0 ? 'red' : undefined }}>{formatCurrency(row.amount, row.currency)}</Box>
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('commons.table_actions'),
      getActions: (params) => {
        const row = params.row as BillingRow
        return [
          <IconButton disabled={readOnly} size="small" onClick={() => onEditClick(row.id)} color="primary">
            <FontAwesomeIcon icon={faPen} style={{ fontSize: '1rem' }} />
          </IconButton>,
        ]
      },
    },
  ]
}

function useRows(documents: ReservationBillDocumentF[]): BillingRow[] {
  const { currency } = useTenant()
  const rows = useMemo(() => {
    const elements: BillingRow[] = documents.map((d) => {
      return {
        id: d.id,
        numberExtended: d.numberExtended,
        fiscalType: d.fiscalType,
        date: d.documentDate,
        currency: currency,
        amount: d.totalAmount,
        customer: d.customer,
      }
    })

    return elements
  }, [JSON.stringify(documents)])

  return rows
}
